import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { passwordAPI, formatApiError } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import NavBar from '../components/NavBar';
import { Mail, KeyRound, Sparkles, Lock } from 'lucide-react';

export default function ForgotPassword() {
  const [searchParams] = useSearchParams();
  const urlToken = searchParams.get('token') || '';
  const [step, setStep] = useState(urlToken ? 'reset' : 'request');
  const [email, setEmail] = useState('');
  const [token, setToken] = useState(urlToken);
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [devToken, setDevToken] = useState(null);

  useEffect(() => {
    if (urlToken) { setToken(urlToken); setStep('reset'); }
  }, [urlToken]);

  const requestReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await passwordAPI.forgot(email);
      toast.success('Şifre sıfırlama talimatı gönderildi.');
      if (data?.dev_token) {
        setDevToken(data.dev_token);
        setToken(data.dev_token);
      }
      setStep('reset');
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  const doReset = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error('Şifre en az 6 karakter olmalı'); return; }
    setLoading(true);
    try {
      await passwordAPI.reset(token, password);
      toast.success('Şifren güncellendi. Şimdi giriş yapabilirsin.');
      window.location.href = '/login';
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <NavBar />
      <div className="relative max-w-md mx-auto px-4 py-14">
        <div className="hero-glow absolute inset-0 pointer-events-none" />
        <div className="aurora-card rounded-3xl p-8 relative">
          <div className="flex items-center gap-2 mb-6">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-fuchsia-500 to-indigo-500 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold">Şifre Sıfırlama</h1>
              <p className="text-xs text-muted-foreground">{step === 'request' ? 'E-postani gir, sifirlama baglantisi alacaksin.' : 'Yeni sifreni belirle.'}</p>
            </div>
          </div>

          {step === 'request' ? (
            <form onSubmit={requestReset} className="space-y-4" data-testid="forgot-form">
              <div className="space-y-1.5">
                <Label htmlFor="email">E-posta</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input id="email" type="email" value={email} onChange={(e)=>setEmail(e.target.value)} placeholder="ornek@mail.com" className="pl-9 bg-white/5 border-white/10" data-testid="forgot-email-input" required />
                </div>
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-gradient-to-r from-fuchsia-500 to-indigo-500" data-testid="forgot-submit-btn">
                {loading ? 'Gönderiliyor...' : 'Sıfırlama Talebi Gönder'}
              </Button>
            </form>
          ) : (
            <form onSubmit={doReset} className="space-y-4" data-testid="reset-form">
              {devToken && (
                <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-xs text-amber-200">
                  <strong>Dev Token (e-posta servisi henuz aktif degil):</strong>
                  <code className="block mt-1 break-all">{devToken}</code>
                </div>
              )}
              {!urlToken && (
                <div className="space-y-1.5">
                  <Label htmlFor="token">Token</Label>
                  <div className="relative">
                    <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input id="token" value={token} onChange={(e)=>setToken(e.target.value)} className="pl-9 bg-white/5 border-white/10" data-testid="reset-token-input" required />
                  </div>
                </div>
              )}
              <div className="space-y-1.5">
                <Label htmlFor="password">Yeni Şifre <span className="text-xs text-muted-foreground">(min 6 karakter)</span></Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input id="password" type="password" value={password} onChange={(e)=>setPassword(e.target.value)} className="pl-9 bg-white/5 border-white/10" data-testid="reset-password-input" required minLength={6} />
                </div>
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-gradient-to-r from-fuchsia-500 to-indigo-500" data-testid="reset-submit-btn">
                {loading ? 'Güncelleniyor...' : 'Şifremi Güncelle'}
              </Button>
            </form>
          )}

          <p className="text-center text-sm text-muted-foreground mt-6">
            <Link to="/login" className="text-fuchsia-400 hover:text-fuchsia-300">Giriş ekranına dön</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
